import './style/statsActions.scss';

import {Loader, parseMoney} from '@dvrd/dvr-controls';
import React, {MouseEventHandler, useMemo} from 'react';
import Subscription from '../../../../models/subscriptionModel';
import {DiscountType, SubscriptionStatus} from '../../../../util/interfaces';
import classNames from 'classnames';
import InfoBanner from '../../../misc/infoBanner';

interface Props {
    onOpenCustomerDetails: MouseEventHandler;
    onClickPortal: MouseEventHandler;
    onClickCoupon: MouseEventHandler;
    onClickFailedPayment: MouseEventHandler;
    onClickStart: MouseEventHandler;
    onCancelSubscription: MouseEventHandler;
    orderCount: number;
    loading: boolean;
    subscription: Subscription;
}

type SubscriptionAction = { label: string; onClick: MouseEventHandler };

export default function StatsActions(props: Props) {
    const {
        orderCount, loading, subscription, onOpenCustomerDetails, onClickPortal, onClickCoupon, onClickFailedPayment,
        onClickStart, onCancelSubscription
    } = props;

    const actions = useMemo(() => {
        const {status, failedPayment, paymentMethods} = subscription;
        if (status === SubscriptionStatus.FREE) return [];
        const actions: Array<SubscriptionAction> = [
            {label: 'Gegevens wijzigen', onClick: onOpenCustomerDetails},
            {label: 'Facturen bekijken', onClick: onClickPortal},
            {label: 'Coupon toepassen', onClick: onClickCoupon}
        ];
        if (failedPayment?.paymentUrl.length) actions.splice(0, 0, {
            label: 'Openstaande factuur betalen', onClick: onClickFailedPayment
        });
        else if (failedPayment) actions.splice(0, 0, {
            label: 'Openstaande factuur betalen', onClick: onClickFailedPayment
        });
        else if (status === SubscriptionStatus.INACTIVE || !paymentMethods.length) return actions.concat([
            {label: 'Abonnement starten', onClick: onClickStart}
        ]);
        else {
            const startStopAction = status === SubscriptionStatus.CANCELED ? onClickStart : onCancelSubscription;
            const startStopLabel = status === SubscriptionStatus.CANCELED ? 'starten' : 'stopzetten';
            actions.push(
                {label: `Abonnement ${startStopLabel}`, onClick: startStopAction},
                {label: 'Betaalwijze aanpassen', onClick: onClickStart}
            );
        }
        return actions;
    }, [subscription.status, subscription.paymentMethods, subscription.failedPayment]);

    function renderSmallLoader() {
        return <Loader active={loading} size='2.5rem' thickness='.1rem'/>;
    }

    function renderOrderCount() {
        return (
            <div className='block invoice-count'>
                {renderSmallLoader()}
                <label className='block-title'>Aantal orders</label>
                <label className='block-sub'>afgelopen 30 dagen</label>
                <label className='count'>{orderCount}</label>
            </div>
        );
    }

    function renderStatus() {
        const {status, isTrialing} = subscription;
        let statusClass: string;
        if (isTrialing && ![SubscriptionStatus.ACTIVE, SubscriptionStatus.CANCELED].includes(status))
            statusClass = 'trialing';
        else statusClass = status;

        return (
            <div className='block status'>
                {renderSmallLoader()}
                <label className='block-title'>Status</label>
                <label className='block-sub'>huidig abonnement</label>
                <div className='row'>
                    <label className={classNames('sub-status', statusClass)}>{subscription.statusLabel}</label>
                    {renderDiscount()}
                </div>
                {renderPaymentMethod()}
                {(isTrialing && subscription.trialEndDate) &&
                    <label className='trial-end-date'>{renderTrialText()}</label>}
                {renderDiscountDuration()}
            </div>
        );
    }

    function renderDiscount() {
        if (!subscription.hasActiveDiscount()) return null;
        const {discount, discountType} = subscription;
        if (discountType === DiscountType.PERCENT)
            return <label className='sub-status blue'>{discount}% korting</label>;
        return <label className='sub-status blue sm'>&euro;{parseMoney(discount)} korting</label>;
    }

    function renderPaymentMethod() {
        if (!subscription.hasPaymentMethod) return null;
        const paymentMethod = subscription.paymentMethods[0];
        return (
            <div className='payment-details'>
                <label>Betaalmethode:&nbsp;</label>
                <label>{paymentMethod.displayPaymentType}</label>
                <label>Rekeningnummer:&nbsp;</label>
                <label>{paymentMethod.bank}<span className='stars'>****</span>{paymentMethod.accountNumber}</label>
            </div>
        );
    }

    function renderTrialText() {
        const {status, trialEndDate} = subscription;
        if (status === SubscriptionStatus.ACTIVE)
            return `Proef nog tot ${trialEndDate!.format('DD-MM-YYYY')}, daarna betaald`;
        return `tot ${trialEndDate!.format('DD-MM-YYYY')}`;
    }

    function renderDiscountDuration() {
        const {discountUntil} = subscription;
        if (!discountUntil || !subscription.hasActiveDiscount()) return null;
        return <label className='trial-end-date'>Korting geldig tot {discountUntil.format('DD-MM-YYYY')}</label>;
    }

    function renderActionsContainer() {
        return (
            <div className='block actions'>
                {renderSmallLoader()}
                <label className='block-title'>Huidig abonnement</label>
                {renderActions()}
            </div>
        );
    }

    function renderActions() {
        if (!actions.length) return null;
        return actions.map((action, index) => (
            <label key={index} className='subscription-action' onClick={action.onClick}>
                {action.label}
            </label>
        ));
    }

    return (
        <div className='subscription-stats-actions'>
            {renderOrderCount()}
            {renderStatus()}
            {renderActionsContainer()}
        </div>
    );
}

StatsActions.displayName = 'StatsActions';
